import { Table, Text } from '@mantine/core';
import React from 'react';
import { formatMoney, TenantCurrency } from 'src/common/currency';
import { formatDate } from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FinancialRow } from './financialRows';
import { AssetFeedRowIcon } from 'src/financial/financial-data/AssetFeedIndicators';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { formatOwnerShare } from 'src/financial/financial-data/ownerShares';

// Named owners win where the view spans the family, since no single member is "me" there and an
// item can belong to several. Otherwise the individual view shows the client's own share, and a
// view carrying neither falls back to the member's name.
const ownershipDisplay = (row: FinancialRow) => {
  if (row.ownerNames && row.ownerNames.length > 0) return row.ownerNames.join(', ');
  if (!row.ownerShare) return row.memberName;
  return formatOwnerShare(row.ownerShare);
};

// A joint item reads as the share counted (the client's, or on the family view the household's),
// with the whole value under it, so the row explains why it contributes less to the totals than
// the figure the adviser entered. The individual view already says "(assumed)" beside the
// client's share; the family view names owners there instead, so it says so under the value.
const fullValueSubtext = (row: FinancialRow, currency: TenantCurrency) => {
  if (row.fullBalance == null) return undefined;
  const whole = `of ${formatMoney(row.fullBalance, currency)}`;
  return row.isShareAssumed && !row.ownerShare ? `${whole}, shares assumed` : whole;
};

export function FinancialRows({
  rows,
  onRowClick,
  showOwnership = false,
  ownershipLabel = 'Ownership',
  showPolicyNumber = false,
}: {
  rows: FinancialRow[];
  onRowClick?: (row: FinancialRow) => void;
  showOwnership?: boolean;
  ownershipLabel?: string;
  showPolicyNumber?: boolean;
}) {
  const currency = useCurrency();
  const columns = [
    ...(showOwnership ? [ownershipLabel] : []),
    'Description',
    'Provider',
    ...(showPolicyNumber ? ['Policy number'] : []),
    'Type',
    'Valuation date',
    'Valuation',
  ];

  return (
    <Table
      withRowBorders={false}
      verticalSpacing={0}
      horizontalSpacing={0}
      style={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}
      styles={{ thead: { backgroundColor: 'transparent' } }}
    >
      <Table.Thead>
        <Table.Tr>
          {columns.map((column, index) => (
            <Table.Th
              key={column}
              style={{ padding: 8, textAlign: index === columns.length - 1 ? 'right' : 'left' }}
            >
              <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.5">
                {column}
              </Text>
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map(row => (
          <Table.Tr
            key={row.id}
            style={{ cursor: onRowClick ? 'pointer' : undefined }}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {showOwnership && <RowCell>{ownershipDisplay(row)}</RowCell>}
            <RowCell>
              {row.description || '—'}
              <AssetFeedRowIcon
                isUpdatedAutomatically={row.isUpdatedAutomatically}
                canConnectToFeed={row.canConnectToFeed}
              />
            </RowCell>
            <RowCell>{row.provider}</RowCell>
            {showPolicyNumber && <RowCell>{row.policyNumber}</RowCell>}
            <RowCell>{row.typeLabel}</RowCell>
            <RowCell>{row.balanceDate ? formatDate(row.balanceDate) : undefined}</RowCell>
            <RowCell align="right" subtext={fullValueSubtext(row, currency)}>
              {row.balance != null ? formatMoney(row.balance, currency) : undefined}
            </RowCell>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function RowCell({
  children,
  subtext,
  align = 'left',
}: {
  children: React.ReactNode;
  subtext?: string;
  align?: 'left' | 'right';
}) {
  return (
    <Table.Td style={{ padding: 8, textAlign: align }}>
      <Text size="xs" fw={FONT_WEIGHTS.medium} c="black">
        {children || '—'}
      </Text>
      {subtext && (
        <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.5">
          {subtext}
        </Text>
      )}
    </Table.Td>
  );
}
